import { useSyncExternalStore } from 'react';

/**
 * Blog overview filter state, held in the URL.
 *
 * The filter bar and every category feed are separate React roots (one per Block Grid widget), so they
 * cannot share a context the way regulator-marine's single Next.js tree did. The query string is the one
 * place they can all read: ?category=<key>&author=<key,key>&type=<key,key>. Writing it with pushState
 * also gives back/forward navigation and shareable links for free.
 */
export type BlogFilterState = {
    category: string;
    authors: string[];
    types: string[];
};

const CHANGE_EVENT = 'blogoverviewfilterchange';
const KEY_LENGTH = 32;
// Matches BlogController's cap, so a URL the store accepts is one the API accepts.
const MAX_KEYS = 20;

export const PARAM_CATEGORY = 'category';
export const PARAM_AUTHOR = 'author';
export const PARAM_TYPE = 'type';

function isHexDigit(c: string): boolean {
    return (c >= '0' && c <= '9') || (c >= 'a' && c <= 'f');
}

/** A key as the index stores it: 32 lowercase hex characters, no dashes. */
export function isKey(value: string): boolean {
    if (value.length !== KEY_LENGTH) {
        return false;
    }
    for (let i = 0; i < value.length; i++) {
        if (!isHexDigit(value.charAt(i))) {
            return false;
        }
    }
    return true;
}

function parseKeyList(value: string | null): string[] {
    const keys: string[] = [];
    if (!value) {
        return keys;
    }
    const parts = value.split(',');
    const count = Math.min(parts.length, MAX_KEYS);
    for (let i = 0; i < count; i++) {
        const key = parts[i].trim().toLowerCase();
        if (isKey(key) && keys.indexOf(key) < 0) {
            keys.push(key);
        }
    }
    return keys;
}

/** Reads the filter out of a query string. Exported for tests; components use useBlogFilter. */
export function parseBlogFilter(search: string): BlogFilterState {
    const params = new URLSearchParams(search);
    const category = (params.get(PARAM_CATEGORY) ?? '').trim().toLowerCase();
    return {
        category: isKey(category) ? category : '',
        authors: parseKeyList(params.get(PARAM_AUTHOR)),
        types: parseKeyList(params.get(PARAM_TYPE)),
    };
}

// useSyncExternalStore needs the same object back while nothing changed, or it re-renders forever.
let cachedSearch: string | null = null;
let cachedState: BlogFilterState = { category: '', authors: [], types: [] };

function getSnapshot(): BlogFilterState {
    const search = window.location.search;
    if (search !== cachedSearch) {
        cachedSearch = search;
        cachedState = parseBlogFilter(search);
    }
    return cachedState;
}

function subscribe(onChange: () => void): () => void {
    window.addEventListener('popstate', onChange);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
        window.removeEventListener('popstate', onChange);
        window.removeEventListener(CHANGE_EVENT, onChange);
    };
}

/**
 * One string per filter, so the server-rendered first page can be matched to the URL the client reads.
 * Must produce the same string as BlogOverviewUrlFilter.Key (Seed.Backoffice.Extensions/Blog).
 */
export function filterKey(state: BlogFilterState): string {
    return state.category + '|' + state.authors.join(',') + '|' + state.types.join(',');
}

export function useBlogFilter(): BlogFilterState {
    return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Replaces the filter in the URL, keeping every other query parameter, and tells every subscribed root.
 * An empty value removes its parameter rather than leaving "?category=".
 */
export function setBlogFilter(next: BlogFilterState): void {
    const params = new URLSearchParams(window.location.search);
    setOrDelete(params, PARAM_CATEGORY, isKey(next.category) ? next.category : '');
    setOrDelete(params, PARAM_AUTHOR, next.authors.filter(isKey).slice(0, MAX_KEYS).join(','));
    setOrDelete(params, PARAM_TYPE, next.types.filter(isKey).slice(0, MAX_KEYS).join(','));

    const query = params.toString();
    const url = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    if (url === window.location.pathname + window.location.search + window.location.hash) {
        return;
    }
    window.history.pushState(null, '', url);
    window.dispatchEvent(new Event(CHANGE_EVENT));
}

function setOrDelete(params: URLSearchParams, name: string, value: string): void {
    if (value) {
        params.set(name, value);
    } else {
        params.delete(name);
    }
}
